/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {TextField} from '@material-ui/core';
import {isEmpty as isEmptyObj} from 'lodash';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import isNull from 'lodash.isnull';
import moment from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {getAncestorsAccount} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  getCodeMappings,
  getErrorCode,
  hasDiagCode
} from '@ion/core/src/services/JobMetaDefinitions';
import {
  Application,
  Device,
  Devices,
  EntityStatus,
  GenericObject,
  Groups,
  ICNApplications
} from '@ion/core/src/types/entities';
import {
  APIGatewaySearch,
  APIGatewaySmartSearch,
  SmartSearchCriteria
} from '@ion/core/src/types/services';
import {calendarDate, formatDateTime} from '@ion/core/src/utils/DateUtil';
import {getLastReportedDate} from '@ion/dls/src/utils/GridUtils';

/*
 * Defines the required percentage of tasks which must be completed.
 */
const MINIMUM_PERCENTAGE = 15;

export const onRenderOption = ({name} = {}) => {
  return <span title={name}>
    {name?.length > 40 ? name?.substring(0, 40) + '...' : name}
  </span>;
};

export const onFileNameRenderOption = ({name, account} = {}) => {
  const value = name?.length > 40 ? name.substring(0, 40) : (name || '');
  const title = account?.title || '';
  return <span title={name}>
    {`${value} (${title})`}
  </span>;
};

export const onRenderInput = (entry: string) => {
  return function(params) {
    return <TextField {...params}
      inputProps={{...params?.inputProps}}
      placeholder={translate(entry)} />;
  };
};

/**
 * Returns the determined last id report value based on the given flags.
 *
 * If explicitLastReport is true, this will pull the lastIdReport from the device extensions
 * 'idReportTimestamp' field ONLY, and if unavailable will return a blank string.
 *
 * If explicitLastReport flag is false, this function will strategically determine the field,
 * defaulting to others before ultimately returning a blank string if none exist.
 */
export const getLastIdReport = (device: Device, explicitLastReport: boolean, latestIdReport): string => {
  if (explicitLastReport) {
    return formatDateTime(device?.extensions?.idReportTimestamp);
  }
  return getLastReportedDate(device, latestIdReport);
};

// Builds the smart search for a bulk service enablement request
export const getTotalRowsSearch = (search: SmartSearchCriteria): APIGatewaySmartSearch => ({
  smartSearch : {
    search : {...search.searchCriteria[0].smartSearch}
  }
});

// Builds a search request for a bulk service enablement call with the ids of the given devices.
export const getDeviceIdsSearch = (selectedDevices: Devices): APIGatewaySearch => ({
  searchTerm : {
    search : {
      searchTerms : {
        id : selectedDevices?.map(({id}) => id).join(',')
      }
    }
  }
});

export const getHeartBeatInfo = (heartBeatInfo, accountId) => {
  if (heartBeatInfo.length > 0) {
    return {
      accountId     : accountId,
      deviceIdList  : heartBeatInfo,
      accountIdType : 'AccountType',
      deviceIdType  : 'ESN'
    };
  }
};

export const checkICNRole = (applications: Application[]) => {
  return applications?.find(({href}) => {
    const appKey = href?.substring(href?.lastIndexOf('/') + 1);
    return isEqual(appKey, ICNApplications.FirstNoticeOfLoss);
  })?.status === EntityStatus.Enabled;
};

export const getICNHeartBeatData = (devices: Device[], applications: Application[]): string[] => {
  if (checkICNRole(applications) && devices?.length) {
    return devices.filter(({esn}) => !isNull(esn)).map(({esn}) => esn);
  }
  return [];
};

export const getAlertTime = (startDate, endDate, format = 'YYYY-MM-DDTHH:mm:ss.SSSZ'): string => {
  const startDateTime = moment(startDate).format(format);
  const endDateTime   = moment(endDate).endOf('day').format(format);
  return `[${startDateTime} TO ${endDateTime}]`;
};

export const getICNInfo = (
  isHeartbeat: boolean,
  applications: Application[]
): boolean => {
  if (isHeartbeat) {
    return checkICNRole(applications);
  }
  return false;
};

export const createAncestorList = (currentGroupId: number): Promise<GenericObject> => {
  if (!isNil(currentGroupId)) {
    return getAncestorsAccount(currentGroupId).then(result => result);
  }
};

/**
 * Returns ids by batch to perform group search service to retrieve inheritable 
 * property from response.
 */
export const getBatchIds = (devices: Device[]) => {
  const uniqueIds       = new Set();
  const batches         = [];
  const limit           = 100;
  let currentBatchLimit = 0;
  let currentBatch      = '';

  devices?.forEach(({groups = {}}) => {
    groups?.forEach(({href = {}}) => {
      const hrefs = entityId(href);
      if (uniqueIds?.has(hrefs)) {
        return false;
      } if (currentBatchLimit >= limit) {
        batches.push(currentBatch?.slice(0, -1));
        currentBatch      = '';
        currentBatchLimit = 0;
      }
      currentBatch += hrefs + ',';
      currentBatchLimit++;
      uniqueIds?.add(hrefs);
    });
  });

  return {
    currentBatch,
    batches
  };
};

/**
 * Retuns devices array with updating inheritable property.
 */
const getGroups = (device: Device, inheritableGroups: Groups): Groups => {
  return device?.groups?.map(group => {
    const matchedGroup = inheritableGroups?.find(inheritable =>
      entityId(inheritable?._self?.href) === entityId(group?.href)
    );

    if (matchedGroup) {
      return {
        ...group,
        inheritable : matchedGroup?.inheritable,
        account     : matchedGroup?.account
      };
    }
    return group;
  });
};

/**
 * Returns ancestors groups results.
 */
const filterGroups = (updatedGroups: Groups, ancestorsGroups) => {
  return updatedGroups?.filter(({account, inheritable = {}}) => {
    if (inheritable) {
      return !ancestorsGroups?.some(({href}) => entityId(account?.href) === entityId(href));
    }
    return true;
  });
};

/**
 * Returns devices with updated groups results.
 */
export const getUpdatedGroups = (devices: Device[], inheritableGroups, ancestorsGroups) => {
  let filteredGroups      = null;
  const deviceswithGroups = [];

  devices?.map(device => {
    const updatedGroups = getGroups(device, inheritableGroups);
    filteredGroups      = ancestorsGroups?.length ? filterGroups(updatedGroups, ancestorsGroups) : updatedGroups;
    deviceswithGroups.push({...device, groups: filteredGroups || {}});
  });

  return deviceswithGroups;
};

/**
 * The utility checks the progress of a task and returns its percentage. If task is Cancelled,
 * empty string is returned.
 * 
 * @returns {string} - Percentage of the task (Completed, Failed, Pending and Remaining).
 */
export const getJobTasksDetails = ({
  total = 0,
  failed = 0,
  pending = 0,
  completed = 0,
  cancelled = 0
} = {}) => {
  let calculatePercent = '';
  if (total !== cancelled) {
    const activeTotal = total - cancelled;
    const calculate   = (value: number) => {
      return activeTotal ? Math.round((value / activeTotal) * 100) : 0;
    };

    const completedPercent  = calculate(completed);
    const failedPercent     = calculate(failed);
    const inProgressPercent = calculate(pending);

    if (total !== cancelled) {
      if (completedPercent > MINIMUM_PERCENTAGE) {
        calculatePercent += `${completedPercent}%`;
      } if (failedPercent > MINIMUM_PERCENTAGE) {
        calculatePercent += `${failedPercent}%`;
      } if (inProgressPercent > MINIMUM_PERCENTAGE) {
        calculatePercent += `${inProgressPercent}%`;
      }
    }
    return calculatePercent.trim();
  }
  return calculatePercent;
};

/**
 * Returns the Job details.
 */
export const getJobDetails = (items) => {
  const {
    jobId,
    username,
    description,
    tasks,
    account,
    progressStatus,
    createdOn
  } = items || {};

  return {
    jobId      : jobId,
    tasks      : tasks?.length,
    devices    : tasks?.[0]?.counts?.total,
    account    : account?.title,
    createdOn  : calendarDate(createdOn),
    jobCreator : username,
    description,
    progressStatus
  };
};

/**
 * Returns the Tasks details.
 */
export const getTaskDetails = (tasks) => {
  const taskData = [];

  tasks?.map(({
    counts: {
      total, completed, pending, failed, cancelled
    }, description = {}, progress: {
      status = {}
    } = {}
  }, index) => {
    taskData.push({
      task            : `${translate('jobsearchengine:taskDetails.task')} ${index+1}`,
      taskDescription : description,
      devicesCount    : total,
      completed       : completed,
      pending         : pending,
      failed          : failed,
      status          : status,
      details         : getJobTasksDetails({total, failed, pending, completed, cancelled}),
      cancelled       : cancelled
    });
  });

  return taskData;
};

/**
 * Returns the Devices details based on matched task index.
 */
export const getDeviceDetails = (matchedDevices) => {
  const deviceData = [];

  const {
    statCode,
    diagCode,
    diagCode32Bit
  } = getCodeMappings();

  matchedDevices?.map?.(({
    device: {
      esn = {}, serialNumber = {}
    } = {}, taskStatus: {
      status = {}, errorCodes = {}, updateTime = null
    } = {}, job : {
      creationDate = null
    } = {}, identifier = {}, error = {}
  }) => (deviceData.push({
    identifier   : getIdentifiers(esn, serialNumber, identifier),
    deviceStatus : status,
    reason       : hasDiagCode(errorCodes?.[0]) ? getErrorCode({
      error : errorCodes?.[0],
      statCode,
      diagCode,
      diagCode32Bit
    }) : errorCodes?.[0],
    lastUpdated : formatDateTime(updateTime || creationDate) || translate('common:notAvailable')
  })));

  return deviceData;
};

/**
 * Returns headers for each tab.
 */
export const getHeadersForTab = (columns, tabName) => {
  const tab = columns?.find(tab => tab?.tabName === tabName);
  return tab ? tab.columns : [];
};

/**
 * Returns the first available identifier, ignoring empty objects.
 */
export const getIdentifiers = (...values) => {
  return values?.find?.(value => value && typeof value !== 'object' && !isEmptyObj(value));
};
